/**
 * procurementGoodAllocationApi — RTK Query slice cho /purchase/good-allocations/*
 * (Procurement Goods Allocation, Access frmProcurementMainSubFormGoodAlc).
 * Tag types: 'ProcGoodAlc' (list) + 'ProcGoodAlcDetail' (single).
 */
import { baseApi } from './baseApi'
import type {
  DataResponse,
  GoodAllocationCheckTruckId,
  GoodAllocationDeleteMany,
  GoodAllocationListItem,
  GoodAllocationListQuery,
  GoodAllocationLookupResponse,
  GoodAllocationContractSnapshot,
  GoodAllocationReceiptSnapshot,
  GoodAllocationSuggestTruckId,
  GoodAllocationTruckStatusChange,
  GoodAllocationWrite,
  MessageResponse,
  PageResponse,
} from '@/types/api'

export const procurementGoodAllocationApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listGoodAllocations: b.query<PageResponse<GoodAllocationListItem>, GoodAllocationListQuery | void>({
      query: (params) => ({ url: '/purchase/good-allocations/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({ type: 'ProcGoodAlc' as const, id: it.id })),
              { type: 'ProcGoodAlc' as const, id: 'LIST' },
            ]
          : [{ type: 'ProcGoodAlc' as const, id: 'LIST' }],
    }),
    getGoodAllocationForm: b.query<GoodAllocationListItem, number>({
      query: (id) => `/purchase/good-allocations/${id}/form`,
      providesTags: (_r, _e, id) => [{ type: 'ProcGoodAlcDetail', id }],
    }),
    createGoodAllocation: b.mutation<DataResponse<number>, GoodAllocationWrite>({
      query: (body) => ({ url: '/purchase/good-allocations', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcGoodAlc', id: 'LIST' }],
    }),
    updateGoodAllocation: b.mutation<MessageResponse, { id: number; body: GoodAllocationWrite }>({
      query: ({ id, body }) => ({ url: `/purchase/good-allocations/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'ProcGoodAlc', id: arg.id },
        { type: 'ProcGoodAlc', id: 'LIST' },
        { type: 'ProcGoodAlcDetail', id: arg.id },
      ],
    }),
    deleteGoodAllocation: b.mutation<MessageResponse, number>({
      query: (id) => ({ url: `/purchase/good-allocations/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'ProcGoodAlc', id },
        { type: 'ProcGoodAlc', id: 'LIST' },
      ],
    }),
    deleteManyGoodAllocations: b.mutation<MessageResponse, GoodAllocationDeleteMany>({
      query: (body) => ({ url: '/purchase/good-allocations/delete-many', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcGoodAlc', id: 'LIST' }],
    }),
    changeGoodAllocationTruckStatus: b.mutation<MessageResponse, GoodAllocationTruckStatusChange>({
      query: (body) => ({ url: '/purchase/good-allocations/change-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcGoodAlc', id: 'LIST' }],
    }),
    addManyGoodAllocations: b.mutation<DataResponse<number[]>, GoodAllocationWrite[]>({
      query: (rows) => ({ url: '/purchase/good-allocations/add-many', method: 'POST', body: rows }),
      invalidatesTags: [{ type: 'ProcGoodAlc', id: 'LIST' }],
    }),
    suggestGoodAllocationTruckId: b.query<GoodAllocationSuggestTruckId, void>({
      query: () => '/purchase/good-allocations/suggest/truck-id',
      keepUnusedDataFor: 0,
    }),
    checkGoodAllocationTruckId: b.query<GoodAllocationCheckTruckId, { truckId: string; excludeId?: number }>({
      query: ({ truckId, excludeId }) => ({
        url: '/purchase/good-allocations/check/truck-id',
        params: { truck_id: truckId, ...(excludeId ? { exclude_id: excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    printGoodAllocationBbgn: b.query<{ title: string; html: string; message: string }, number>({
      query: (id) => `/purchase/good-allocations/${id}/print/bbgn`,
      keepUnusedDataFor: 0,
    }),
    // Lookups
    goodAllocPContractLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/p-contract',
    }),
    goodAllocConsignmentLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/consignment',
    }),
    goodAllocGradeLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/grade',
    }),
    goodAllocWarehouseLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/warehouse',
    }),
    goodAllocInspectionLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/inspection',
    }),
    goodAllocTruckStatusLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/truck-status',
    }),
    goodAllocReceiptGrnLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/receipt-grn',
    }),
    goodAllocReceiptRdnLookup: b.query<GoodAllocationLookupResponse, void>({
      query: () => '/purchase/good-allocations/lookups/receipt-rdn',
    }),
    // Snapshot cascades (Access *_AfterUpdate) — fetch khi pick combo.
    goodAllocContractSnapshot: b.query<
      GoodAllocationContractSnapshot,
      { contractNo: string; kind: 'p' | 'consignment' }
    >({
      query: ({ contractNo, kind }) => ({
        url: '/purchase/good-allocations/lookups/contract-snapshot',
        params: { contract_no: contractNo, kind },
      }),
      keepUnusedDataFor: 0,
    }),
    goodAllocReceiptSnapshot: b.query<
      GoodAllocationReceiptSnapshot,
      { receiptNo: string; kind: 'GRN' | 'RDN' }
    >({
      query: ({ receiptNo, kind }) => ({
        url: '/purchase/good-allocations/lookups/receipt-snapshot',
        params: { receipt_no: receiptNo, kind },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useListGoodAllocationsQuery,
  useGetGoodAllocationFormQuery,
  useLazyGetGoodAllocationFormQuery,
  useCreateGoodAllocationMutation,
  useUpdateGoodAllocationMutation,
  useDeleteGoodAllocationMutation,
  useDeleteManyGoodAllocationsMutation,
  useChangeGoodAllocationTruckStatusMutation,
  useAddManyGoodAllocationsMutation,
  useSuggestGoodAllocationTruckIdQuery,
  useLazySuggestGoodAllocationTruckIdQuery,
  useLazyPrintGoodAllocationBbgnQuery,
  useLazyCheckGoodAllocationTruckIdQuery,
  useGoodAllocPContractLookupQuery,
  useGoodAllocConsignmentLookupQuery,
  useGoodAllocGradeLookupQuery,
  useGoodAllocWarehouseLookupQuery,
  useGoodAllocInspectionLookupQuery,
  useGoodAllocTruckStatusLookupQuery,
  useGoodAllocReceiptGrnLookupQuery,
  useGoodAllocReceiptRdnLookupQuery,
  useLazyGoodAllocContractSnapshotQuery,
  useLazyGoodAllocReceiptSnapshotQuery,
} = procurementGoodAllocationApi
